import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  Briefcase,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Filter,
  MoreHorizontal
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const TeamPlanningPage = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // week, month
  const [teamMembers, setTeamMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedMember, setSelectedMember] = useState('all');

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, tasksRes, projectsRes] = await Promise.all([
        api.get('/users/?role=team_member'),
        api.get('/tasks/'),
        api.get('/projects/')
      ]);
      
      setTeamMembers(membersRes.data || []);
      setTasks(tasksRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get week days
  const getWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Get month days
  const getMonthDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Add days from previous month to start on Monday
    const startDay = firstDay.getDay() || 7;
    for (let i = startDay - 1; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add days to complete the grid (6 rows x 7 days)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  // Navigate
  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get tasks for a specific date and member
  const getTasksForDate = (date, memberId = null) => {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(task => {
      const taskDate = task.deadline?.split('T')[0];
      const matchesDate = taskDate === dateStr;
      const matchesMember = !memberId || memberId === 'all' || task.assigned_to === memberId;
      return matchesDate && matchesMember;
    });
  };

  // Get workload for a member on a date
  const getWorkload = (memberId, date) => {
    const memberTasks = getTasksForDate(date, memberId);
    const totalHours = memberTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    if (totalHours === 0) return { level: 'free', hours: 0 };
    if (totalHours <= 4) return { level: 'light', hours: totalHours };
    if (totalHours <= 7) return { level: 'normal', hours: totalHours };
    return { level: 'heavy', hours: totalHours };
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric',
      month: 'short'
    });
  };

  const formatMonthYear = () => {
    return currentDate.toLocaleDateString('fr-FR', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDays = getWeekDays();
  const monthDays = getMonthDays();

  // Calculate team stats
  const teamStats = {
    totalMembers: teamMembers.length,
    activeTasks: tasks.filter(t => t.status !== 'completed').length,
    completedToday: tasks.filter(t => {
      const today = new Date().toISOString().split('T')[0];
      return t.status === 'completed' && t.updated_at?.startsWith(today);
    }).length,
    averageWorkload: teamMembers.length > 0 
      ? Math.round(tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0) / teamMembers.length)
      : 0
  };

  // Get color for workload level
  const getWorkloadColor = (level) => {
    switch (level) {
      case 'free': return 'bg-gray-100';
      case 'light': return 'bg-green-100 border-green-300';
      case 'normal': return 'bg-blue-100 border-blue-300';
      case 'heavy': return 'bg-orange-100 border-orange-300';
      default: return 'bg-gray-100';
    }
  };

  const filteredMembers = selectedMember === 'all' 
    ? teamMembers 
    : teamMembers.filter(m => m.id === selectedMember);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${theme.colors?.accent}20` }}
              >
                <Calendar className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              </div>
              Planning Équipe
            </h1>
            <p className="text-[#64748b] mt-1">
              Visualisez la charge de travail de votre équipe
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Member Filter */}
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tous les membres" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les membres</SelectItem>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.first_name} {member.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View Mode */}
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Semaine</SelectItem>
                <SelectItem value="month">Mois</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{teamStats.totalMembers}</p>
                  <p className="text-xs text-[#64748b]">Membres actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-50">
                  <Briefcase className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{teamStats.activeTasks}</p>
                  <p className="text-xs text-[#64748b]">Tâches en cours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{teamStats.completedToday}</p>
                  <p className="text-xs text-[#64748b]">Terminées aujourd'hui</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{teamStats.averageWorkload}h</p>
                  <p className="text-xs text-[#64748b]">Charge moyenne</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar Navigation */}
        <Card className="card mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => navigate(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Aujourd'hui
                </Button>
              </div>
              
              <h2 className="text-lg font-semibold capitalize" style={{ color: theme.colors?.primary }}>
                {formatMonthYear()}
              </h2>

              {/* Legend */}
              <div className="hidden md:flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                  Libre
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
                  Léger
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
                  Normal
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
                  Chargé
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
              </div>
            ) : viewMode === 'week' ? (
              /* Week View */
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-3 text-left text-sm font-medium text-[#64748b] border-b w-48">
                        Membre
                      </th>
                      {weekDays.map((day, i) => (
                        <th 
                          key={i} 
                          className={cn(
                            "p-3 text-center text-sm font-medium border-b min-w-[100px]",
                            isToday(day) ? "bg-[var(--color-accent)]/10" : ""
                          )}
                          style={{ color: isToday(day) ? theme.colors?.accent : '#64748b' }}
                        >
                          <div className="flex flex-col items-center">
                            <span className="capitalize">{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                            <span className={cn(
                              "w-7 h-7 rounded-full flex items-center justify-center mt-1",
                              isToday(day) && "bg-[var(--color-accent)] text-white font-bold"
                            )}>
                              {day.getDate()}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-[#64748b]">
                          Aucun membre dans l'équipe
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map(member => (
                        <tr key={member.id} className="hover:bg-[#f8fafc]">
                          <td className="p-3 border-b">
                            <div className="flex items-center gap-3">
                              <Avatar 
                                name={`${member.first_name} ${member.last_name}`}
                                size="sm"
                                type="creative"
                              />
                              <div>
                                <p className="font-medium text-sm text-[#1a365d]">
                                  {member.first_name} {member.last_name}
                                </p>
                                <p className="text-xs text-[#64748b]">
                                  {member.hourly_rate || 0}€/h
                                </p>
                              </div>
                            </div>
                          </td>
                          {weekDays.map((day, i) => {
                            const workload = getWorkload(member.id, day);
                            const dayTasks = getTasksForDate(day, member.id);
                            
                            return (
                              <td 
                                key={i} 
                                className={cn(
                                  "p-2 border-b text-center",
                                  isToday(day) && "bg-[var(--color-accent)]/5"
                                )}
                              >
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div 
                                        className={cn(
                                          "h-16 rounded-lg border flex flex-col items-center justify-center cursor-pointer transition-all hover:scale-105",
                                          getWorkloadColor(workload.level)
                                        )}
                                      >
                                        {workload.hours > 0 ? (
                                          <>
                                            <span className="text-lg font-bold text-[#1a365d]">
                                              {workload.hours}h
                                            </span>
                                            <span className="text-xs text-[#64748b]">
                                              {dayTasks.length} tâche{dayTasks.length > 1 ? 's' : ''}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-xs text-[#94a3b8]">—</span>
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      {dayTasks.length > 0 ? (
                                        <div className="space-y-2">
                                          {dayTasks.map(task => (
                                            <div key={task.id} className="text-sm">
                                              <p className="font-medium">{task.name}</p>
                                              <p className="text-xs text-gray-400">
                                                {task.estimated_hours || 0}h estimées
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p>Aucune tâche prévue</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Month View */
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-[#64748b]">
                    {day}
                  </div>
                ))}
                
                {/* Days */}
                {monthDays.map(({ date, isCurrentMonth }, i) => {
                  const dayTasks = getTasksForDate(date);
                  const totalHours = dayTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
                  
                  return (
                    <TooltipProvider key={i}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className={cn(
                              "min-h-[80px] p-2 rounded-lg border transition-all cursor-pointer",
                              isCurrentMonth ? "bg-white" : "bg-gray-50",
                              isToday(date) && "ring-2 ring-[var(--color-accent)]",
                              dayTasks.length > 0 && "hover:shadow-md"
                            )}
                          >
                            <div className={cn(
                              "text-sm font-medium mb-1",
                              isCurrentMonth ? "text-[#1a365d]" : "text-[#94a3b8]",
                              isToday(date) && "text-[var(--color-accent)]"
                            )}>
                              {date.getDate()}
                            </div>
                            
                            {dayTasks.length > 0 && (
                              <div className="space-y-1">
                                <div className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                  {dayTasks.length} tâche{dayTasks.length > 1 ? 's' : ''}
                                </div>
                                {totalHours > 0 && (
                                  <div className="text-xs text-[#64748b]">
                                    {totalHours}h planifiées
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium mb-1">
                            {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                          {dayTasks.length > 0 ? (
                            <div className="space-y-1">
                              {dayTasks.slice(0, 5).map(task => (
                                <div key={task.id} className="text-sm">
                                  • {task.name} ({task.estimated_hours || 0}h)
                                </div>
                              ))}
                              {dayTasks.length > 5 && (
                                <div className="text-xs text-gray-400">
                                  +{dayTasks.length - 5} autres tâches
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">Aucune tâche</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Members List */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Équipe ({teamMembers.length} membres)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(member => {
                const memberTasks = tasks.filter(t => t.assigned_to === member.id && t.status !== 'completed');
                const totalHours = memberTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
                
                return (
                  <div 
                    key={member.id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-[#e2e8f0] hover:shadow-md transition-all"
                  >
                    <Avatar 
                      name={`${member.first_name} ${member.last_name}`}
                      size="md"
                      type="creative"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1a365d] truncate">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-sm text-[#64748b]">
                        {memberTasks.length} tâche{memberTasks.length > 1 ? 's' : ''} • {totalHours}h
                      </p>
                    </div>
                    <div className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      totalHours === 0 && "bg-gray-100 text-gray-600",
                      totalHours > 0 && totalHours <= 20 && "bg-green-100 text-green-700",
                      totalHours > 20 && totalHours <= 35 && "bg-blue-100 text-blue-700",
                      totalHours > 35 && "bg-orange-100 text-orange-700"
                    )}>
                      {totalHours === 0 ? 'Libre' : 
                       totalHours <= 20 ? 'Léger' : 
                       totalHours <= 35 ? 'Normal' : 'Chargé'}
                    </div>
                  </div>
                );
              })}
              
              {teamMembers.length === 0 && (
                <div className="col-span-full text-center py-8 text-[#64748b]">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun membre dans l'équipe</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => window.location.href = '/creatives'}
                  >
                    Gérer les créatifs
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TeamPlanningPage;
